import {
  afterNextRender,
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from "@angular/core";
import { Chip } from "primeng/chip";
import { ChipOverflowComponent } from "./chip-overflow.component";
import { MAX_VISIBLE_CHIPS } from "./chip.const";
import { VisibleChipsPipe } from "./visible-chips.pipe";

// The selection row of a multi-value p-select: as many chips as fit on one
// line (at most MAX_VISIBLE_CHIPS) plus the "+ X more" tail. Goes inside the
// `#selectedItem` template.
//
// The fit is measured, not guessed: every candidate chip is also rendered in a
// hidden off-flow row at its natural width, and the visible count is the
// largest prefix whose chips, gaps and tail fit the label. Re-measured when
// the selection changes, when the label resizes and once web fonts land.
//
// `label` and `key` are called on every check, so pass stable references —
// an arrow-function field on the host, never a method (it would lose `this`)
// and never an inline arrow (a new reference each check).
@Component({
  selector: "app-selected-chips",
  template: `
    <!-- Tracked by key: p-chip hides itself on remove, so a view reused for
         another item would stay invisible. -->
    @for (item of items() | visibleChips: visibleCount(); track key()(item)) {
      <p-chip
        [label]="label()(item)"
        [removable]="removable()"
        removeIcon="pi pi-times"
        (onRemove)="remove($event, item)"
      />
    }
    <app-chip-overflow [items]="items()" [visibleCount]="visibleCount()" />

    <!-- Measuring copy: every candidate chip plus the widest tail the
         selection could produce, invisible and out of the flow. -->
    <span #measure class="measure" aria-hidden="true">
      @for (item of items() | visibleChips; track key()(item)) {
        <p-chip
          [label]="label()(item)"
          [removable]="removable()"
          removeIcon="pi pi-times"
        />
      }
      <app-chip-overflow [items]="items()" [visibleCount]="1" />
    </span>
  `,
  // The chip row is laid out by the p-select label (see ng-prime/_select.scss);
  // stay out of the flex flow so the chips remain its direct children.
  styles: `
    :host {
      display: contents;
    }

    .measure {
      position: absolute;
      top: 0;
      left: 0;
      display: flex;
      flex-wrap: nowrap;
      align-items: center;
      gap: var(--space-4);
      width: max-content;
      visibility: hidden;
      pointer-events: none;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [Chip, ChipOverflowComponent, VisibleChipsPipe],
})
export class SelectedChipsComponent<T> {
  readonly items = input<T[] | null | undefined>([]);
  readonly label = input.required<(item: T) => string>();
  readonly key = input<(item: T) => unknown>((item) => item);
  readonly removable = input(true);

  readonly removeItem = output<T>();

  protected readonly visibleCount = signal(MAX_VISIBLE_CHIPS);

  private readonly measure =
    viewChild.required<ElementRef<HTMLElement>>("measure");
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  constructor() {
    const destroyRef = inject(DestroyRef);

    afterRenderEffect(() => {
      this.items();
      untracked(() => this.fit());
    });

    afterNextRender(() => {
      // The host is `display: contents`, so the label is its parent.
      const label = this.host.nativeElement.parentElement;
      if (!label) {
        return;
      }

      // Stays outside the Angular zone: the signal write schedules a tick only
      // when the count changes, whereas zone.run() would force a full app tick
      // on every resize frame.
      const observer = new ResizeObserver(() => this.fit());
      observer.observe(label);
      destroyRef.onDestroy(() => observer.disconnect());

      // The first measurement may run against the fallback font.
      void document.fonts?.ready.then(() => this.fit());
    });
  }

  // The click (or Enter/Backspace) would otherwise bubble to the p-select host
  // and open the overlay.
  protected remove(event: Event, item: T): void {
    event.stopPropagation();
    this.removeItem.emit(item);
  }

  private fit(): void {
    const row = this.measure().nativeElement;
    const label = this.host.nativeElement.parentElement;
    if (!label) {
      return;
    }

    const total = this.items()?.length ?? 0;
    const chips = Array.from(row.querySelectorAll<HTMLElement>(".p-chip"));
    const tailWidth =
      row.querySelector<HTMLElement>("app-chip-overflow")?.offsetWidth ?? 0;
    const labelStyle = getComputedStyle(label);
    const available =
      label.clientWidth -
      parseFloat(labelStyle.paddingLeft) -
      parseFloat(labelStyle.paddingRight);
    const gap = parseFloat(getComputedStyle(row).columnGap) || 0;

    let count = 0;
    let used = 0;
    for (const chip of chips) {
      const width = chip.offsetWidth + (0 < count ? gap : 0);
      const tail = count + 1 < total ? gap + tailWidth : 0;
      if (0 < count && available < used + width + tail) {
        break;
      }
      used += width;
      count++;
    }

    // Never drop to zero: a single over-wide chip truncates instead.
    this.visibleCount.set(Math.max(1, count));
  }
}
